import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE } from '@/config/api';
import { OrderIdBadge } from './shared';

interface Order {
  id: string;
  order_number?: string;
  customerName?: string;
  customerPhone?: string;
  orderType?: string;
  garmentType?: string;
  status: string;
  amount?: number;
  orderDate?: string;
  dueDate?: string;
  description?: string;
  measurements?: {
    chest?: number;
    waist?: number;
    hips?: number;
    shoulder?: number;
    sleeve?: number;
    neck?: number;
    inseam?: number;
  };
  specifications?: string[];
  progress?: {
    stage: string;
    percentage: number;
    lastUpdated: string;
  };
  progress_steps?: any[];
}

const OrderDetails: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        setError('No order ID provided');
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE}/api/orders/${orderId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setOrder(data);
        } else {
          // Handle different error cases properly
          if (response.status === 401 || response.status === 403) {
            setError('Authentication required. Please log in again.');
          } else if (response.status === 404) {
            setError('Order not found.');
          } else {
            setError('Failed to fetch order details. Please try again.');
          }
        }
      } catch (_err) {
        setError('Error fetching order details');
        console.error('Error fetching order:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The requested order could not be found.'}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-gray-600 mr-4"
              >
                <span className="text-2xl">←</span>
              </button>
              <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
                <span className="text-white text-lg font-bold">VT</span>
              </div>
              <span className="ml-3 text-xl font-bold text-gray-900">Order Details</span>
            </div>
            <div className="flex items-center space-x-4">
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                Update Status
              </button>
              <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                Mark Complete
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Order Info Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center space-x-3">
                  <h2 className="text-2xl font-bold text-gray-900">Order</h2>
                  <OrderIdBadge orderNumber={order.order_number} id={order.id} />
                </div>
                <p className="text-gray-600">{order.garmentType || order.orderType}</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${getStatusColor(order.status)}`}>
                  {order.status}
                </span>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Customer Information</h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center">
                      <span className="mr-2">👤</span>
                      {order.customerName || 'Customer Name Not Available'}
                    </p>
                    <p className="flex items-center">
                      <span className="mr-2">📞</span>
                      {order.customerPhone || 'Phone Not Available'}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Order Information</h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex justify-between">
                      <span>Order Date:</span>
                      <span className="font-semibold">
                        {order.orderDate ? formatDate(order.orderDate) : 'Not Available'}
                      </span>
                    </p>
                    <p className="flex justify-between">
                      <span>Due Date:</span>
                      <span className="font-semibold">
                        {order.dueDate ? formatDate(order.dueDate) : 'Not Available'}
                      </span>
                    </p>
                    <p className="flex justify-between">
                      <span>Amount:</span>
                      <span className="font-semibold">
                        {order.amount ? formatCurrency(order.amount) : 'Not Available'}
                      </span>
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Progress</h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex justify-between">
                      <span>Current Stage:</span>
                      <span className="font-semibold">{order.progress.stage}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Progress:</span>
                      <span className="font-semibold">{order.progress.percentage}%</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Last Updated:</span>
                      <span className="font-semibold">{formatDate(order.progress.lastUpdated)}</span>
                    </p>
                  </div>
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${order.progress.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Measurements and Specifications */}
          <div className="lg:col-span-2 space-y-6">
            {/* Measurements */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Measurements</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600">Chest</div>
                  <div className="font-semibold">{order.measurements.chest}"</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600">Waist</div>
                  <div className="font-semibold">{order.measurements.waist}"</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600">Hips</div>
                  <div className="font-semibold">{order.measurements.hips}"</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600">Shoulder</div>
                  <div className="font-semibold">{order.measurements.shoulder}"</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600">Sleeve</div>
                  <div className="font-semibold">{order.measurements.sleeve}"</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600">Neck</div>
                  <div className="font-semibold">{order.measurements.neck}"</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600">Inseam</div>
                  <div className="font-semibold">{order.measurements.inseam}"</div>
                </div>
              </div>
            </div>

            {/* Specifications */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Specifications</h3>
              <div className="space-y-2">
                {order.specifications.map((spec, index) => (
                  <div key={index} className="flex items-center p-3 bg-gray-50 rounded">
                    <span className="mr-3">✓</span>
                    <span className="text-sm">{spec}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Work Progress Timeline */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Work Progress</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm mr-4">
                    ✓
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">Order Received</h4>
                    <p className="text-sm text-gray-600">{formatDate(order.orderDate)}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm mr-4">
                    ✓
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">Fabric Selection</h4>
                    <p className="text-sm text-gray-600">Completed</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm mr-4">
                    ⚡
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">Cutting</h4>
                    <p className="text-sm text-gray-600">In Progress</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-sm mr-4">
                    ○
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-400">Sewing</h4>
                    <p className="text-sm text-gray-400">Pending</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-sm mr-4">
                    ○
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-400">Fitting</h4>
                    <p className="text-sm text-gray-400">Pending</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 text-sm mr-4">
                    ○
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-400">Final Delivery</h4>
                    <p className="text-sm text-gray-400">Pending</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails; 
